/**
 * Urgency tiers and deadline escalation (ROUTE-004 … ROUTE-007).
 *
 * Deadline = service cutoff = court date − serviceCutoffDaysBeforeCourt.
 * "Days remaining" counts calendar days from today (agency local) to cutoff.
 */
import { addDaysIso, daysUntil, localParts } from '../time.ts';
import { getPaperType } from './engine.ts';
import type { RuleSetConfig } from './types.ts';

export type UrgencyTier = 'normal' | 'late_received_priority' | 'supervisor_urgent';

export const URGENCY_LABELS: Record<UrgencyTier, string> = {
  normal: 'Normal',
  late_received_priority: 'Late-Received Priority',
  supervisor_urgent: 'Supervisor / Urgent',
};

export interface UrgencyInput {
  paperTypeCode: string;
  /** YYYY-MM-DD court/return date, if any */
  courtDate: string | null;
  receivedAt: Date;
  /** Supervisor marked urgent manually. */
  manualUrgent: boolean;
  /** Supervisor removed the automatic Late-Received flag. */
  lateReceivedRemoved: boolean;
  /** In-person attempts made so far (any outcome, excluding voided). */
  attemptCount: number;
  /** Paper already served / non-served / closed — no escalation. */
  complete: boolean;
}

export interface UrgencyResult {
  tier: UrgencyTier;
  /** 0–100, rises progressively as the cutoff nears. */
  score: number;
  cutoffDate: string | null;
  daysRemaining: number | null;
  lateReceived: boolean;
  autoEscalated: boolean;
  /** Supervisor alert: ≤ escalate days remaining with zero attempts. */
  supervisorAlert: boolean;
  /** Server reminder threshold hit today (10/7/3/1 by default), else null. */
  serverReminderDay: number | null;
  reasons: string[];
}

export function computeUrgency(rules: RuleSetConfig, input: UrgencyInput, now: Date): UrgencyResult {
  const type = getPaperType(rules, input.paperTypeCode);
  const today = localParts(now, rules.timezone).date;
  const received = localParts(input.receivedAt, rules.timezone).date;
  const cutoffDate = input.courtDate ? addDaysIso(input.courtDate, -type.serviceCutoffDaysBeforeCourt) : null;
  const daysRemaining = cutoffDate ? daysUntil(today, cutoffDate) : null;
  const reasons: string[] = [];

  const lateReceived =
    rules.urgency.lateReceivedAutoPriority && cutoffDate !== null && received >= cutoffDate && !input.lateReceivedRemoved;
  if (lateReceived) reasons.push('Received inside service cutoff.');

  const autoEscalated =
    !input.complete && daysRemaining !== null && daysRemaining <= rules.urgency.escalateAtDaysRemaining && input.attemptCount === 0;
  if (autoEscalated) reasons.push(`${daysRemaining} day(s) to cutoff with zero attempts.`);
  if (input.manualUrgent) reasons.push('Marked urgent by supervisor.');

  let tier: UrgencyTier = 'normal';
  if (lateReceived) tier = 'late_received_priority';
  if (input.manualUrgent || autoEscalated) tier = 'supervisor_urgent';

  const tierBase = { normal: 0, late_received_priority: 40, supervisor_urgent: 70 }[tier];
  let proximity = 0;
  if (daysRemaining !== null && !input.complete) {
    const horizon = 30;
    proximity = Math.round(Math.min(1, Math.max(0, (horizon - daysRemaining) / horizon)) * 30);
  }
  const score = input.complete ? 0 : Math.min(100, tierBase + proximity);

  const serverReminderDay =
    !input.complete && daysRemaining !== null && rules.urgency.serverReminderDays.includes(daysRemaining) ? daysRemaining : null;

  return {
    tier,
    score,
    cutoffDate,
    daysRemaining,
    lateReceived,
    autoEscalated,
    supervisorAlert: autoEscalated,
    serverReminderDay,
    reasons,
  };
}
